import { NavLink } from "react-router-dom";
import {
  MODULES_CATALOG,
  CATEGORY_LABELS,
  isModuleEnabled,
  isModuleVisible,
  type ModuleCategory,
} from "@nxvolt/shared";
import { useAuth } from "../lib/auth-context.js";
import {
  ICON_BY_SLUG,
  IconGeneric,
  IconChevronsLeft,
  IconChevronsRight,
} from "./icons.js";

interface SidebarProps {
  /** Ativa modo drawer (mobile). */
  drawer?: boolean;
  /** Estado atual do drawer, controla a classe [data-open]. */
  open?: boolean;
  /** Callback ao clicar num item de navegação — usado para fechar o drawer. */
  onNavigate?: () => void;
  /**
   * Sidebar colapsada (só ícones). Ignorado no modo drawer (mobile) porque
   * lá o "colapsar" não faz sentido — se o drawer está fechado, não se vê
   * nem os ícones.
   */
  collapsed?: boolean;
  /** Toggle do estado colapsado. Botão só é renderizado se este callback existir. */
  onToggleCollapsed?: () => void;
}

export function Sidebar({
  drawer = false,
  open = false,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: SidebarProps = {}) {
  const { user } = useAuth();
  if (!user) return null;

  const allowed = new Set(user.modules);
  const visible = MODULES_CATALOG.filter(
    (m) =>
      isModuleEnabled(m.slug) &&
      isModuleVisible(m.slug) &&
      (user.role === "admin" || allowed.has(m.slug)),
  ).sort((a, b) => a.order - b.order);

  const byCategory = visible.reduce<Record<ModuleCategory, typeof visible>>(
    (acc, m) => {
      (acc[m.category] ||= [] as typeof visible).push(m);
      return acc;
    },
    {
      principal: [],
      carregadores: [],
      operacao: [],
      gestao_eletrica: [],
      frota: [],
      financeiro: [],
      admin: [],
    },
  );

  // No modo drawer (mobile) o colapso não se aplica — o drawer é
  // "aberto/fechado", não "expandido/colapsado".
  const isCollapsed = collapsed && !drawer;

  const cls =
    (drawer ? "nx-sidebar nx-sidebar--drawer" : "nx-sidebar") +
    (isCollapsed ? " nx-sidebar--collapsed" : "");

  return (
    <nav
      id={drawer ? "nx-sidebar-drawer" : undefined}
      className={cls}
      aria-label="Menu principal"
      // data-open só faz sentido em modo drawer; no desktop a sidebar é
      // sempre visível pelo grid do shell.
      {...(drawer ? { "data-open": open ? "true" : "false" } : {})}
      // Quando fechado, remove do fluxo de foco por teclado. Ainda existe
      // no DOM pra o transform funcionar sem re-mount.
      aria-hidden={drawer && !open ? true : undefined}
    >
      {onToggleCollapsed && !drawer && (
        <div className="nx-sidebar__toolbar">
          <button
            type="button"
            className="nx-sidebar__collapse-btn"
            onClick={onToggleCollapsed}
            aria-label={isCollapsed ? "Expandir menu" : "Recolher menu"}
            aria-expanded={!isCollapsed}
            title={isCollapsed ? "Expandir menu" : "Recolher menu"}
          >
            {isCollapsed ? <IconChevronsRight /> : <IconChevronsLeft />}
          </button>
        </div>
      )}

      {(Object.keys(byCategory) as ModuleCategory[]).map((cat) => {
        const items = byCategory[cat];
        if (items.length === 0) return null;
        return (
          <section key={cat} className="nx-sidebar__section">
            {/* Cabeçalho da categoria some no modo colapsado — a label
                é o que dava sentido a ele. Manter espaçamento entre grupos
                fica por conta da margem do próprio ul/section.            */}
            {!isCollapsed && (
              <h2 className="nx-sidebar__heading">{CATEGORY_LABELS[cat]}</h2>
            )}
            <ul>
              {items.map((m) => {
                // Ícone SVG por slug. Emoji do catalog fica só como fallback
                // de última hora se um módulo novo aparecer antes do mapa.
                const Icon = ICON_BY_SLUG[m.slug] ?? IconGeneric;
                return (
                  <li key={m.slug}>
                    <NavLink
                      to={`/app/${m.slug}`}
                      onClick={onNavigate}
                      // Sem tabIndex quando fechado: quem navega por teclado
                      // não fica pulando itens escondidos.
                      tabIndex={drawer && !open ? -1 : undefined}
                      // Título nativo do browser é o fallback de acessibilidade
                      // caso o tooltip custom não renderize (motion-reduce etc.).
                      title={isCollapsed ? m.name : undefined}
                      aria-label={isCollapsed ? m.name : undefined}
                      className={({ isActive }) =>
                        "nx-sidebar__link" +
                        (isActive ? " nx-sidebar__link--active" : "")
                      }
                    >
                      <Icon />
                      <span className="nx-sidebar__label">{m.name}</span>
                      {isCollapsed && (
                        <span className="nx-sidebar__tooltip" role="tooltip">
                          {m.name}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </nav>
  );
}
